/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { useEffect, useState } from 'react';
import { type ExternalToast } from 'sonner';

import { toast, Toaster, type ToastId, type ToastVariants } from './Toast';

type ControlledToast = {
  id: ToastId;
  text: string;
  onClose?: (id: ToastId) => void;
  actionLabel?: string;
  onAction?: (id: ToastId) => void;
} & (
  | {
      variant: Exclude<ToastVariants, 'progress-bar' | 'loading-spinner'>;
    }
  | {
      id: ToastId;
      variant: Extract<ToastVariants, 'progress-bar'>;
      progressBarValue: number;
    }
);
type ToastControlledProps = {
  toasts: ControlledToast[];
} & Pick<React.ComponentProps<typeof Toaster>, 'visibleToasts'>;

const toastWithVariant = (input: ControlledToast) => {
  const commonRootProps = {
    actionLabel: input.actionLabel,
    isCloseable: input.onClose !== undefined,
    isControlled: true,
    onAction(id: ToastId) {
      input.onAction?.(id);
    },
    onClose(id: ToastId) {
      input.onClose?.(id);
    },
    shouldAutoClose: false,
    shouldCloseOnAction: false,
  };

  const commonSonnerProps: ExternalToast = {
    dismissible: false,
    id: input.id,
  };

  switch (input.variant) {
    case 'neutral':
      return toast.neutral(input.text, {
        ...commonRootProps,
        sonnerToastProps: commonSonnerProps,
      });
    case 'success':
      return toast.success(input.text, {
        ...commonRootProps,
        sonnerToastProps: commonSonnerProps,
      });
    case 'danger':
      return toast.danger(input.text, {
        ...commonRootProps,
        sonnerToastProps: commonSonnerProps,
      });
    case 'progress-bar':
      return toast.progressBar(input.text, {
        ...commonRootProps,
        initialProgressBarValue: input.progressBarValue,
        sonnerToastProps: commonSonnerProps,
      });
  }
};

export const ToasterControlled = ({
  toasts,
  ...restProps
}: ToastControlledProps) => {
  const [toastsState, setToastsState] = useState<ControlledToast[]>([]);

  useEffect(() => {
    // if a toast is removed, close it
    const removedToasts = toastsState.filter(
      (toast) => !toasts.some((item) => item.id === toast.id),
    );
    removedToasts.forEach((removedToast) => {
      toast.close(removedToast.id);
    });

    // Otherwise, update the toast or create a new one
    for (const toast of toasts) {
      toastWithVariant(toast);
    }

    setToastsState(toasts);
  }, [toasts, toastsState]);

  return <Toaster visibleToasts={1} {...restProps} />;
};
